import { getDominantEntityIncidence } from 'DataDiscovery/discoverApi';

export const NO_FINDINGS_ENTITY = 'No findings';

/**
 * Flatten a discovery results `tables` array into CSV rows — one per column, so the export
 * is a complete inventory of what was scanned, not only what was found. Columns (and tables)
 * without a finding are kept with entity "No findings", count 0 and percentage 0.0; a table
 * with no fields at all still gets one row with a blank field.
 *
 * `count` is the dominant entity's instance count; `percentage` is the same display rule as
 * the table panel (percent of sampled rows, one decimal, capped at 100). Metadata-only findings
 * have no sampled instances, so their percentage is blank.
 */
export function buildCsvRows(tables, { dataSource = '', database = '', schema = '' } = {}) {
  const rows = [];
  (Array.isArray(tables) ? tables : []).forEach((t) => {
    const rowsSampled = t.rows_sampled ?? 0;
    const base = { data_source: dataSource, database, schema, table: t.name ?? '' };
    const fields = Array.isArray(t.fields) ? t.fields : [];
    if (fields.length === 0) {
      rows.push({ ...base, field: '', entity: NO_FINDINGS_ENTITY, count: 0, percentage: '0.0' });
      return;
    }
    fields.forEach((f) => {
      const findings = f.findings || {};
      const { dominantEntity, dominantCount, isMetadataOnly } = Object.keys(findings).length > 0
        ? getDominantEntityIncidence(findings, rowsSampled)
        : {};
      if (!dominantEntity) {
        rows.push({ ...base, field: f.name ?? '', entity: NO_FINDINGS_ENTITY, count: 0, percentage: '0.0' });
        return;
      }
      const percentage = isMetadataOnly
        ? ''
        : Math.min(100, (dominantCount / rowsSampled) * 100).toFixed(1);
      rows.push({
        ...base,
        field: f.name ?? '',
        entity: dominantEntity,
        count: dominantCount ?? 0,
        percentage,
      });
    });
  });
  return rows;
}
